import type { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router";
import { SkyNavDrawer, SkyLink, SkyText } from "@skypointcloud/skypoint-ui";
import { useMsalAuth } from "../../../hooks/useMsalAuth";
import { useNewDocumentSubmissions } from "../../../hooks/useNewDocumentSubmissions";
import type { NavId } from "./sidebarConfig";
import { getNavRoute, getSelectedNavItemInShell } from "./routeMapping";
import { settingsHubSelection, withSettingsCollapsed } from "./settingsHub";
import { useInboxUnreadCount, useShellNavSections } from "./useShellNavSections";

/**
 * skypoint-ui 2.4.0 dropped SkyNavDrawer's `showCopyright` prop in favour of a
 * generic `footer` slot, so the copyright line the sidebar has always shown is
 * now rendered here. Markup mirrors what the library used to emit (centred
 * SkyText at size 200, brand link to skypoint.ai); the drawer still supplies
 * the surrounding `mt-auto border-t py-3 px-3` wrapper and still hides the
 * footer when collapsed.
 *
 * D7 (2026-10-08 shell spec): never clipped. The line wraps in two balanced
 * halves rather than leaving "Reserved." alone, and the drawers below are
 * anchored to the viewport's bottom edge (`bottom-0`) instead of sized by
 * `100vh`, which also counts what covers the bottom of the page (a horizontal
 * scrollbar, a phone browser's toolbar) and so pushed the footer under it.
 */
const sidebarFooter = (
  <SkyText
    size={200}
    align="center"
    block
    className="text-balance"
  >
    Copyright {new Date().getFullYear()}{" "}
    <SkyLink
      href="https://skypoint.ai/"
      target="_blank"
    >
      Skypoint.
    </SkyLink>{" "}
    All Rights Reserved.
  </SkyText>
);

interface SidebarSkyProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCollapseChange?: (collapsed: boolean) => void;
  /** Mobile sheet only: rendered above the nav (the header's Location switcher, D27). */
  mobileTop?: ReactNode;
}

const SidebarSky = ({
  isMobile = false,
  isOpen,
  onOpenChange,
  onCollapseChange,
  mobileTop,
}: SidebarSkyProps) => {
  const { getQueryString } = useMsalAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // The shell's navigation (useShellNavSections), Settings entries included as data: the guard and
  // the tiled Settings page read the same entries (2026-10-08 shell spec, D5).
  const inboxUnreadCount = useInboxUnreadCount();
  const newDocumentSubmissions = useNewDocumentSubmissions();
  const { sections, activeProduct } = useShellNavSections({
    inboxUnreadCount,
    newDocumentSubmissions,
  });
  // D5: the Settings item is drawn as one item that opens the tiled Settings page, never as a
  // group of its entries.
  const drawn = withSettingsCollapsed(sections);

  // Get current selection based on route
  const queryString = getQueryString();
  // Hire's shell highlights exactly as it always has. Another product's shell highlights a shared
  // page (Users, Products) by its own entry for it. Then D5: the Settings item stays lit on the
  // tiled page and on every settings page, shared pages included.
  const { selectedId, selectedCategoryId } = settingsHubSelection(
    location.pathname,
    queryString,
    getSelectedNavItemInShell(location.pathname, queryString, activeProduct, sections),
    sections,
  );

  // Handle nav item selection
  const handleNavSelect = (itemId: string) => {
    const route = getNavRoute(itemId as NavId);
    if (route) {
      navigate(route + queryString);
      // Close mobile sidebar after navigation
      if (isMobile && onOpenChange) {
        onOpenChange(false);
      }
    }
  };

  // Mobile sidebar with backdrop overlay
  if (isMobile) {
    if (!isOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          data-testid="mobile-backdrop"
          className="fixed inset-0 bg-black/50 z-999"
          onClick={() => onOpenChange?.(false)}
        />
        {/* Drawer */}
        <div
          data-testid="mobile-drawer"
          className="fixed top-0 bottom-0 left-0 z-1000 flex flex-col bg-background animate-in slide-in-from-left duration-200"
        >
          {mobileTop && <div className="border-b border-border p-3">{mobileTop}</div>}
          <SkyNavDrawer
            sections={drawn}
            selectedId={selectedId}
            selectedCategoryId={selectedCategoryId}
            onSelect={handleNavSelect}
            size="medium"
            footer={sidebarFooter}
            className="min-h-0! flex-1!"
          />
        </div>
      </>
    );
  }

  // Desktop sidebar - fixed position, visible on md+ screens
  return (
    <div
      id="sky-sidebar"
      className="hidden! md:block!"
    >
      <SkyNavDrawer
        sections={drawn}
        selectedId={selectedId}
        selectedCategoryId={selectedCategoryId}
        onSelect={handleNavSelect}
        size="medium"
        collapsible
        onCollapseChange={onCollapseChange}
        footer={sidebarFooter}
        className="fixed! top-[52px]! bottom-0! left-0! h-auto! z-810! overflow-hidden!"
      />
    </div>
  );
};

export default SidebarSky;
